const SELECTORS = {
  cards: '[acuire-card]',
  images: '[acuire-card-img]',
  swiperGroup: "[data-swiper-group='is--acquire']",
  swiperWrap: "[data-swiper-wrap='is--acquire']",
  swiperPrev: "[data-swiper-prev='is--acquire']",
  swiperNext: "[data-swiper-next='is--acquire']",
  swiperPagination: "[data-swiper-pagination='is--acquire']",
};

const DUR = 0.6;
const EASE = 'power3.inOut';
const TEXT_FADE_OUT = 0.25;
const TEXT_FADE_IN_DELAY = 0.4;

// Webflow CSS values (reverse-engineered from the live stylesheet)
const COLOR_INACTIVE_BG = '#a0a7f5'; // var(--button--purple-medium)
const COLOR_ACTIVE_BG = '#e9ebff';

// ─────────────────────────────────────────────
// Inject center images preload so first swap doesn't stutter
// ─────────────────────────────────────────────
function preloadImages() {
  const imgs = document.querySelectorAll(SELECTORS.images);
  imgs.forEach((img) => {
    if (img.complete) return;
    const preload = new Image();
    if (img.srcset) preload.srcset = img.srcset;
    preload.src = img.src;
  });
}

// ─────────────────────────────────────────────
// Cross-fade the active center image
// ─────────────────────────────────────────────
function syncImage(cardValue, immediate = false) {
  const images = document.querySelectorAll(SELECTORS.images);
  images.forEach((img) => {
    const isMatch = img.getAttribute('acuire-card-img') === cardValue;
    if (immediate) {
      gsap.set(img, { opacity: isMatch ? 1 : 0 });
    } else {
      gsap.to(img, {
        opacity: isMatch ? 1 : 0,
        duration: 0.4,
        ease: EASE,
        overwrite: true,
      });
    }
  });
}

// ─────────────────────────────────────────────
// DESKTOP — class-toggle for width/colors, GSAP for paragraph
// ─────────────────────────────────────────────
function initDesktop() {
  const cards = Array.from(document.querySelectorAll(SELECTORS.cards));
  if (!cards.length) return () => {};

  const slides = cards.map((c) => c.closest('.swiper-slide')).filter(Boolean);
  const wrapperEl = slides[0]?.parentElement;
  if (!wrapperEl) return () => {};

  const getContent = (card) => card.querySelector('.hp-acquire-card-rich-text, p, ul');

  // ─────────────────────────────────────────────
  // Compute flex-grow ratio from Webflow CSS max-widths
  // ─────────────────────────────────────────────
  // Read both CSS-defined max-widths and derive a ratio. The ratio stays
  // constant regardless of viewport (since both rem values scale together
  // with Webflow's fluid root font size), so we only compute it once.
  //
  //   .acuire-card           → max-width: 18.2rem (inactive)
  //   .swiper-slide.is--acquire → max-width: 57.2rem (active)
  //   ACTIVE_GROW = 57.2 / 18.2 ≈ 3.143
  const referenceCard = cards.find((c) => !c.classList.contains('is--active')) || cards[0];
  const inactiveMaxWidth = parseFloat(getComputedStyle(referenceCard).maxWidth) || 182;
  const slideMaxWidth = parseFloat(getComputedStyle(slides[0]).maxWidth) || 572;
  const INACTIVE_GROW = 1;
  const ACTIVE_GROW = slideMaxWidth / inactiveMaxWidth;

  // ─────────────────────────────────────────────
  // Initial state — first card active, content visible
  // ─────────────────────────────────────────────
  cards.forEach((card) => {
    const content = getContent(card);
    if (content) {
      gsap.set(content, {
        height: 0,
        opacity: 0,
        marginTop: 0,
        overflow: 'hidden',
      });
    }
  });

  const firstCard = cards[0];
  const firstValue = firstCard.getAttribute('acuire-card');
  if (!firstCard.classList.contains('is--active')) {
    firstCard.classList.add('is--active');
  }
  const firstContent = getContent(firstCard);
  if (firstContent) {
    gsap.set(firstContent, {
      height: 'auto',
      opacity: 1,
      marginTop: '0.75rem',
      overflow: 'hidden',
    });
  }
  syncImage(firstValue, /* immediate */ true);

  // ─────────────────────────────────────────────
  // Click handler — uses CACHED widths (no per-click measurement)
  // ─────────────────────────────────────────────
  const onClick = function () {
    if (this.classList.contains('is--active')) return;

    const newCard = this;
    const oldCard = cards.find((c) => c.classList.contains('is--active'));
    if (!oldCard || oldCard === newCard) return;

    const newValue = newCard.getAttribute('acuire-card');
    const oldContent = getContent(oldCard);
    const newContent = getContent(newCard);
    const oldSlide = oldCard.closest('.swiper-slide');
    const newSlide = newCard.closest('.swiper-slide');
    if (!oldSlide || !newSlide) return;

    // ── Phase 1: closing card's text fades out FIRST
    if (oldContent) {
      gsap.to(oldContent, {
        opacity: 0,
        duration: TEXT_FADE_OUT,
        ease: EASE,
        overwrite: true,
      });
    }

    // ── Phase 2 (delayed by text-fade window)
    gsap.delayedCall(0.1, () => {
      // Lock paragraph height + overflow:hidden (anti-spill safety)
      if (oldContent) {
        const lockedHeight = oldContent.offsetHeight;
        gsap.set(oldContent, { height: lockedHeight, overflow: 'hidden' });
      }

      // Convert all 4 slides to flex-grow distribution. The wrapper now
      // distributes its available space according to grow ratios — total
      // ALWAYS sums to 100% of available, mathematically. No rounding.
      // No need for justify-content override (no leftover space to distribute).
      slides.forEach((slide, i) => {
        const isActiveNow = cards[i] === oldCard;
        gsap.set(slide, {
          flexGrow: isActiveNow ? ACTIVE_GROW : INACTIVE_GROW,
          flexShrink: 0,
          flexBasis: 0,
          width: 'auto',
          maxWidth: 'none',
          willChange: 'flex-grow',
        });
      });
      // Release card max-width so they fill their slide as the ratio animates
      cards.forEach((card) => {
        gsap.set(card, { maxWidth: 'none' });
      });

      // Toggle classes — Webflow CSS would snap colors instantly, so we
      // immediately override with inline colors matching the OLD visual state.
      oldCard.classList.remove('is--active');
      newCard.classList.add('is--active');

      gsap.set(oldCard, { backgroundColor: COLOR_ACTIVE_BG });
      gsap.set(newCard, { backgroundColor: COLOR_INACTIVE_BG });

      // ── Animate flex-grow ratios + colors in one timeline.
      // Old slide ratio: ACTIVE_GROW → 1   (shrinks proportionally)
      // New slide ratio: 1 → ACTIVE_GROW   (grows proportionally)
      // Total grow at any frame = ACTIVE_GROW + 3×1 = constant. Zero overflow.
      const tl = gsap.timeline({
        defaults: { duration: DUR, ease: EASE, overwrite: true },
        onComplete: () => {
          // Hand back to CSS — clear all inline overrides
          slides.forEach((s) =>
            gsap.set(s, {
              clearProps: 'flexGrow,flexShrink,flexBasis,width,maxWidth,willChange',
            })
          );
          cards.forEach((c) => gsap.set(c, { clearProps: 'maxWidth,backgroundColor' }));
        },
      });

      tl.to(oldSlide, { flexGrow: INACTIVE_GROW }, 0);
      tl.to(newSlide, { flexGrow: ACTIVE_GROW }, 0);
      tl.to(oldCard, { backgroundColor: COLOR_INACTIVE_BG }, 0);
      tl.to(newCard, { backgroundColor: COLOR_ACTIVE_BG }, 0);

      // Closing card's content collapses
      if (oldContent) {
        gsap.to(oldContent, {
          height: 0,
          marginTop: 0,
          duration: DUR,
          ease: EASE,
          overwrite: true,
        });
      }

      // Opening card's content expands
      if (newContent) {
        gsap.set(newContent, { overflow: 'hidden' });
        gsap.fromTo(
          newContent,
          { height: 0, opacity: 0, marginTop: 0 },
          {
            height: 'auto',
            marginTop: '0.75rem',
            duration: DUR,
            ease: EASE,
            overwrite: true,
          }
        );
        gsap.to(newContent, {
          opacity: 1,
          duration: 0.3,
          ease: EASE,
          delay: TEXT_FADE_IN_DELAY,
          overwrite: false, // don't overwrite the height tween above
        });
      }

      // Image swap coordinated with the layout reflow
      syncImage(newValue);
    });
  };

  cards.forEach((card) => card.addEventListener('click', onClick));

  // Teardown
  return () => {
    cards.forEach((card) => {
      card.removeEventListener('click', onClick);
      const content = getContent(card);
      if (content) gsap.set(content, { clearProps: 'all' });
    });
    document.querySelectorAll(SELECTORS.images).forEach((img) => {
      gsap.set(img, { clearProps: 'all' });
    });
  };
}

// ─────────────────────────────────────────────
// MOBILE — Swiper + image cross-fade on slide change
// ─────────────────────────────────────────────
function initMobile() {
  const swiperGroup = document.querySelector(SELECTORS.swiperGroup);
  if (!swiperGroup) return () => {};

  const swiperWrap = swiperGroup.querySelector(SELECTORS.swiperWrap);
  if (!swiperWrap) return () => {};

  const prevButton = swiperGroup.querySelector(SELECTORS.swiperPrev);
  const nextButton = swiperGroup.querySelector(SELECTORS.swiperNext);
  const paginationEl = swiperGroup.querySelector(SELECTORS.swiperPagination);

  const getActiveCardValue = (swiper) => {
    const slide = swiper.slides[swiper.activeIndex];
    if (!slide) return null;
    const card = slide.querySelector(SELECTORS.cards);
    return card ? card.getAttribute('acuire-card') : null;
  };

  const swiperInstance = new Swiper(swiperWrap, {
    slidesPerView: 'auto',
    speed: 600,
    grabCursor: true,
    navigation: { nextEl: nextButton, prevEl: prevButton },
    pagination: { el: paginationEl, type: 'bullets', clickable: true },
    keyboard: { enabled: true, onlyInViewport: false },
    on: {
      init(swiper) {
        const value = getActiveCardValue(swiper);
        if (value) syncImage(value, /* immediate */ true);
      },
      slideChange(swiper) {
        const value = getActiveCardValue(swiper);
        if (value) syncImage(value);
      },
    },
  });

  return () => {
    try {
      swiperInstance.destroy(true, true);
    } catch (e) {
      /* noop */
    }
    document.querySelectorAll(SELECTORS.images).forEach((img) => {
      gsap.set(img, { clearProps: 'all' });
    });
  };
}

// ─────────────────────────────────────────────
// ORCHESTRATOR — uses gsap.matchMedia for clean responsive scoping
// ─────────────────────────────────────────────
export function initHpAcquireCard() {
  preloadImages();

  const mm = gsap.matchMedia();
  let teardown = null;

  mm.add('(min-width: 992px)', () => {
    teardown = initDesktop();
    return () => {
      if (teardown) {
        teardown();
        teardown = null;
      }
    };
  });

  mm.add('(max-width: 991px)', () => {
    teardown = initMobile();
    return () => {
      if (teardown) {
        teardown();
        teardown = null;
      }
    };
  });

  // Top-level teardown
  return () => {
    mm.revert();
  };
}
